import { createContext, useContext, useState, useEffect, useRef } from 'react';
import { authAPI, presenceAPI } from '../lib/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const heartbeatRef = useRef(null);

    useEffect(() => {
        // Returning from Google OAuth: AuthCallback exchanges the session_id first — skip /me check.
        if (window.location.hash?.includes('session_id=')) {
            setLoading(false);
            return;
        }
        checkAuth();
    }, []);

    const setSession = (token, userData) => {
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(userData));
        sessionStorage.setItem('login_at', String(Date.now()));
        sessionStorage.removeItem('withdrawal_timer_notified');
        setUser(userData);
    };

    // Heartbeat: send every 30 seconds while user is logged in
    useEffect(() => {
        if (user) {
            // Send immediately on login
            presenceAPI.heartbeat().catch(() => {});
            
            heartbeatRef.current = setInterval(() => {
                presenceAPI.heartbeat().catch(() => {});
            }, 30000);
            
            return () => {
                if (heartbeatRef.current) clearInterval(heartbeatRef.current);
            };
        }
    }, [user]);

    const checkAuth = async () => {
        const token = localStorage.getItem('token');
        if (token) {
            try {
                const response = await authAPI.getMe();
                setUser(response.data);
            } catch (err) {
                localStorage.removeItem('token');
                localStorage.removeItem('user');
            }
        }
        setLoading(false);
    };

    const login = async (email, password) => {
        try {
            setError(null);
            const response = await authAPI.login({ email, password });
            if (response.data?.requires_2fa) {
                return { success: false, requires_2fa: true, challenge_id: response.data.challenge_id, email: response.data.email };
            }
            const { token, user: userData, must_change_password } = response.data;
            const enriched = { ...userData, must_change_password: !!(must_change_password ?? userData.must_change_password) };
            setSession(token, enriched);
            return { success: true, must_change_password: enriched.must_change_password };
        } catch (err) {
            const message = err.response?.data?.detail || 'Login failed';
            setError(message);
            return { success: false, error: message };
        }
    };

    const verify2fa = async (challengeId, code) => {
        try {
            setError(null);
            const response = await authAPI.verify2fa({ challenge_id: challengeId, code });
            const { token, user: userData, must_change_password } = response.data;
            const enriched = { ...userData, must_change_password: !!(must_change_password ?? userData.must_change_password) };
            setSession(token, enriched);
            return { success: true, must_change_password: enriched.must_change_password };
        } catch (err) {
            const message = err.response?.data?.detail || 'Verification failed';
            setError(message);
            return { success: false, error: message };
        }
    };

    const register = async (name, email, password, extraFields = {}) => {
        try {
            setError(null);
            const response = await authAPI.register({ name, email, password, ...extraFields });
            const { token, user: userData } = response.data;
            localStorage.setItem('token', token);
            localStorage.setItem('user', JSON.stringify(userData));
            setUser(userData);
            return { success: true };
        } catch (err) {
            const message = err.response?.data?.detail || 'Registration failed';
            setError(message);
            return { success: false, error: message };
        }
    };

    const logout = async () => {
        // Mark offline before clearing token
        try {
            await presenceAPI.logoutStatus();
        } catch {}
        try { await authAPI.googleLogout(); } catch {}
        if (heartbeatRef.current) clearInterval(heartbeatRef.current);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
    };

    const refreshUser = async () => {
        try {
            const response = await authAPI.getMe();
            setUser(response.data);
        } catch (err) {
            // Ignore errors
        }
    };

    const value = { setSession,
        user,
        loading,
        error,
        login,
        verify2fa,
        register,
        logout,
        refreshUser,
        isAuthenticated: !!user,
        isAdmin: user?.role === 'admin' || user?.role === 'superadmin',
        isSuperAdmin: user?.role === 'superadmin',
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
